import type { ApprovalMode, VehicleGroupData } from '@fleet/shared';
import { apiFetch } from './client';

export interface VehicleGroup {
  id: string;
  name: string;
  description?: string;
  approvalMode: ApprovalMode;
  colorHex: string;
  isActive: boolean;
  vehicleCount: number;
  canBook: boolean;
  hasSlackConfig: boolean;
}

export const vehicleGroupsApi = {
  list: () => apiFetch<VehicleGroup[]>('/api/v1/vehicle-groups'),

  create: (body: VehicleGroupData) =>
    apiFetch<{ id: string }>('/api/v1/vehicle-groups', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Vollständiges Ersetzen — der Endpunkt ist ein PUT.
   *
   * Ein Teilobjekt zu senden wäre kein „nur diese Felder ändern", sondern ein
   * Zurücksetzen aller weggelassenen: Das Schema setzt seine Defaults ein.
   */
  update: (id: string, body: VehicleGroupData) =>
    apiFetch<void>(`/api/v1/vehicle-groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** Deaktivieren. Die Gruppe bleibt erhalten, weil Buchungen an ihr hängen. */
  deactivate: (id: string) =>
    apiFetch<void>(`/api/v1/vehicle-groups/${id}`, { method: 'DELETE' }),

  /** `null` heißt „nicht eingerichtet" — dann stellt die Gruppe nichts zu. */
  slackConfig: (id: string) =>
    apiFetch<VehicleGroupSlackConfig | null>(`/api/v1/vehicle-groups/${id}/slack-config`),

  /**
   * Setzen. Der Server prüft die Channel-ID bei Slack und übernimmt den Namen —
   * eine ungeprüfte ID wäre die Art Fehler, die am längsten unentdeckt bleibt.
   */
  setSlackConfig: (id: string, body: VehicleGroupSlackConfigInput) =>
    apiFetch<VehicleGroupSlackConfig>(`/api/v1/vehicle-groups/${id}/slack-config`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  removeSlackConfig: (id: string) =>
    apiFetch<void>(`/api/v1/vehicle-groups/${id}/slack-config`, { method: 'DELETE' }),
};

export interface VehicleGroupSlackConfig {
  slackChannelId: string;
  /** Zuletzt bei Slack erfragter Name — nur zur Anzeige. */
  slackChannelName: string;
  approverSlackUserIds: string[];
  approverSlackUserGroupIds: string[];
  notifyOnNewBooking: boolean;
  notifyOnUnmatchedTrip: boolean;
  allowSlackApproval: boolean;
  allowSelfApproval: boolean;
}

/** Was das Formular schickt — ohne Team-ID und Channelnamen, die weiß der Server. */
export type VehicleGroupSlackConfigInput = Omit<VehicleGroupSlackConfig, 'slackChannelName'>;
